import { Clock, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LeadGroup } from '@/domains/types'
import { formatRelativeDate, truncate, getUserColor } from '@/ui/utils/main'
import { classNames } from '@/ui/utils/main'
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card'
import { MarkdownView } from '../common/MarkdownView'

interface LeadGroupListProps {
  groups: LeadGroup[]
  showInvestigationDetails?: boolean
  showSubmitter?: boolean
  maxLeadsPerGroup?: number
  canEdit?: (lead: LeadGroup['leads'][number]) => boolean
  onEdit?: (lead: LeadGroup['leads'][number]) => void
}

export function LeadGroupList({
  groups,
  showInvestigationDetails = true,
  showSubmitter = true,
  maxLeadsPerGroup = 10,
  canEdit,
  onEdit,
}: LeadGroupListProps) {
  const { t } = useTranslation()

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('admin.lead_group_empty')}</p>
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <Card key={group.investigation.id} className="border-none shadow-none">
          {showInvestigationDetails && (
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-primary" />
                    {group.investigation.name}
                  </CardTitle>
                  <MarkdownView
                    content={group.investigation.description}
                    compact
                    className="text-sm text-muted-foreground mt-1"
                  />
                </div>
                <Badge variant="secondary">
                  {t('admin.lead_group_count', { count: group.leads.length })}
                </Badge>
              </div>
            </CardHeader>
          )}

          <CardContent className="pt-0">
            <ul className="space-y-3">
              {group.leads
                .sort(
                  (itemA: LeadGroup['leads'][number], itemB: LeadGroup['leads'][number]) =>
                    new Date(itemA.createdAt).getTime() - new Date(itemB.createdAt).getTime(),
                )
                .slice(0, maxLeadsPerGroup)
                .map((lead: LeadGroup['leads'][number]) => (
                  <LeadItem
                    key={lead.id}
                    lead={lead}
                    showSubmitter={showSubmitter}
                    showTopic={!showInvestigationDetails}
                    canEdit={canEdit}
                    onEdit={onEdit}
                  />
                ))}

              {group.leads.length > maxLeadsPerGroup && (
                <li className="text-sm text-muted-foreground">
                  {t('admin.lead_group_more', { count: group.leads.length - maxLeadsPerGroup })}
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

interface LeadItemProps {
  lead: LeadGroup['leads'][number]
  showSubmitter: boolean
  showTopic: boolean
  canEdit?: (lead: LeadGroup['leads'][number]) => boolean
  onEdit?: (lead: LeadGroup['leads'][number]) => void
}

function LeadItem({ lead, showSubmitter, showTopic, canEdit, onEdit }: LeadItemProps) {
  return (
    <li className="p-3 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <span className="text-sm font-bold text-muted-foreground">
            {(lead.investigation?.name || '?').charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {showTopic && (
              <Badge variant="outline" className="text-xs">
                {lead.investigation?.name}
              </Badge>
            )}
            <span className="font-medium text-foreground truncate flex-1">
              {truncate(lead.title || lead.description, 80)}
            </span>
          </div>

          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {showSubmitter && (
              <div className="flex items-center gap-1">
                <div
                  className="w-5 h-5 rounded-full"
                  style={{ backgroundColor: getUserColor(lead.submitterUsername) }}
                />
                <span>{lead.submitterUsername}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatRelativeDate(lead.createdAt)}</span>
            </div>
          </div>
        </div>

        {onEdit && canEdit?.(lead) !== false && (
          <button
            onClick={() => onEdit(lead)}
            className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors ml-auto shrink-0"
            aria-label="Edit lead"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </li>
  )
}

interface BadgeProps {
  variant?: 'default' | 'secondary' | 'outline' | 'destructive'
  children: React.ReactNode
  className?: string
}

function Badge({ variant = 'default', children, className }: BadgeProps) {
  const badgeClasses = classNames(
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors',
    {
      'bg-primary/10 text-primary-foreground': variant === 'default',
      'bg-secondary/10 text-secondary-foreground': variant === 'secondary',
      'border border-input bg-background': variant === 'outline',
      'bg-destructive/10 text-destructive-foreground': variant === 'destructive',
    },
    className,
  )

  return <span className={badgeClasses}>{children}</span>
}
